//#region IMPORT
import React, { useEffect } from 'react';
import GridFunctions from '../../../../Services/agGrid/ag-Grid-functions';
import { BusinessGridCommon } from '../../../../Components/BusinessGridCommon';
import FilePreview from '../../../../Components/FilePreview/FilePreview';
import { Button, Dialog, DatePicker } from '../../../../Components/MUIs';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  QrCode2 as QrCodeIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  AccountCircle as AccountCircleIcon,
  School as SchoolIcon,
  Shield as ShieldIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { getColumnDefs1 } from './TRA_CUSTOM_02_ColumnDefs';
import TRA_CUSTOM_02_ApprovalModal from './TRA_CUSTOM_02_ApprovalModal';
import TRA_CUSTOM_02_ApproverSignModal from './TRA_CUSTOM_02_ApproverSignModal';
import TRA_CUSTOM_02_ApproverRejectModal from './TRA_CUSTOM_02_ApproverRejectModal';
import TRA_CUSTOM_02_ReturnHistoryModal from './TRA_CUSTOM_02_ReturnHistoryModal';
import SignatureCanvas from 'react-signature-canvas';
import Global from '../../../../Services/Common/Global';
import Commons from '../../../../Services/Common/Common';

//#endregion

function TRA_CUSTOM_02(props) {
  //#region 변수
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [selectedWorkers, setSelectedWorkers] = React.useState([]);
  /** 교육일지 등록 방식 선택 모달 (추가 클릭 시) */
  const [registrationMethodSelectOpen, setRegistrationMethodSelectOpen] =
    React.useState(false);

  /** 자체 보유 파일 등록 — 자체 교육 일지 모달 (단일 파일, 로컬 state만 사용) */
  const selfFileInputRef = React.useRef(null);
  const [selfFileModalOpen, setSelfFileModalOpen] = React.useState(false);
  const [selfFileData, setSelfFileData] = React.useState({
    tpmId: '',
    eduTypeCd: '',
    eduTypeNm: '',
    etcEduNm: '',
    etcEduContents: '',
    eduDt: dayjs().format('YYYY-MM-DD'),
    eduTm: '',
    eduPlace: '',
    eduManager: '',
    eduTargetMCnt: '0',
    eduTargetWCnt: '0',
    eduExecMCnt: '0',
    eduExecWCnt: '0',
    eduNoExecMCnt: '0',
    eduNoExecWCnt: '0',
    eduNoExecReason1: '',
    eduNoExecReason2: '',
    eduNoExecReason3: '',
    eduImageFileName1: '',
    eduImageSaveName1: '',
    eduImageFilePath1: '',
  });
  const [selfFileShowErrors, setSelfFileShowErrors] = React.useState(false);
  const [selfFilePreviewOpen, setSelfFilePreviewOpen] = React.useState(false);

  const selfFilePreviewRow = React.useMemo(() => {
    if (!selfFileData.eduImageFileName1) return [];
    return [
      {
        fileName: selfFileData.eduImageFileName1,
        saveName: selfFileData.eduImageSaveName1,
        previewFilePath: selfFileData.eduImageFilePath1,
      },
    ];
  }, [
    selfFileData.eduImageFileName1,
    selfFileData.eduImageSaveName1,
    selfFileData.eduImageFilePath1,
  ]);

  /** PC사인(참석자 서명) 모달 */
  const [pcSignModalOpen, setPcSignModalOpen] = React.useState(false);
  const [pcSignTargetMember, setPcSignTargetMember] = React.useState(null);
  const [pcSignError, setPcSignError] = React.useState(false);
  const pcSignSignatureRef = React.useRef(null);

  useEffect(() => {
    if (!pcSignModalOpen) return;
    pcSignSignatureRef.current?.clear();
    setPcSignError(false);
  }, [pcSignModalOpen]);

  const ClosePcSignModal = () => {
    setPcSignModalOpen(false);
    setPcSignTargetMember(null);
    setPcSignError(false);
    pcSignSignatureRef.current?.clear();
  };

  const HandlePcSignConfirm = async () => {
    const sig = pcSignSignatureRef.current;
    if (!sig || sig.isEmpty()) {
      setPcSignError(true);
      return;
    }

    setPcSignError(false);

    const signatureDataUrl = sig.toDataURL('image/png');
    const decodedData = atob(signatureDataUrl.split(',')[1]);
    const byteNumbers = new Array(decodedData.length);
    for (let i = 0; i < decodedData.length; i++) {
      byteNumbers[i] = decodedData.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/png' });

    const targetId =
      pcSignTargetMember?.tpdId || pcSignTargetMember?.tpaUserId || 'member';
    const fileName = `pc_sign_${targetId}_${Date.now()}.png`;
    const signatureFile = new File([blob], fileName, { type: 'image/png' });

    const signPath = await props.SaveLogicPcSign?.(signatureFile);
    if (!signPath) {
      props.dispatch?.(
        props.showSnackbar?.({
          message: 'PC사인 저장에 실패했습니다.',
          type: 'error',
        })
      );
      return;
    }

    props.setMembers?.((prev) =>
      (prev || []).map((m) => {
        if (m !== pcSignTargetMember) return m;
        return {
          ...m,
          signYn: 'Y',
          signPath,
          // 기존 리포트/인쇄 로직 호환을 위해 기존 "서명" 값(remark)에도 signPath를 함께 넣습니다.
          remark: signPath,
        };
      })
    );

    ClosePcSignModal();
  };

  const searchInfo = useSelector((state) => state.SearchInfo);
  const columnDefs = getColumnDefs1(props);
  const educationDiaryReadOnly = !!props.educationDiaryReadOnly;

  useEffect(() => {
    if (!props.selfFileEditRowPayload) return;
    const row = props.selfFileEditRowPayload;
    const normalizedEduTypeCd = row.eduTypeCd
      ? String(row.eduTypeCd).replace(/^EG_/, '')
      : '';
    setSelfFileData({
      tpmId: row.tpmId ?? '',
      eduTypeCd: normalizedEduTypeCd,
      eduTypeNm: row.eduTypeNm ?? '',
      etcEduNm: row.etcEduNm ?? row.eduNmNm ?? '',
      etcEduContents: row.etcEduContents ?? row.eduContentsNm ?? '',
      eduDt: row.eduDt
        ? dayjs(row.eduDt).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD'),
      eduTm: row.eduTm ?? '',
      eduPlace: row.eduPlace ?? '',
      eduManager: row.eduManager ?? '',
      eduTargetMCnt: String(row.eduTargetMCnt ?? '0'),
      eduTargetWCnt: String(row.eduTargetWCnt ?? '0'),
      eduExecMCnt: String(row.eduExecMCnt ?? '0'),
      eduExecWCnt: String(row.eduExecWCnt ?? '0'),
      eduNoExecMCnt: String(row.eduNoExecMCnt ?? '0'),
      eduNoExecWCnt: String(row.eduNoExecWCnt ?? '0'),
      eduNoExecReason1: row.eduNoExecReason1 ?? '',
      eduNoExecReason2: row.eduNoExecReason2 ?? '',
      eduNoExecReason3: row.eduNoExecReason3 ?? '',
      eduImageFileName1:
        row.selfImageFileName ??
        row.SelfImageFileName ??
        row.SELF_IMAGE_FILE_NAME ??
        row.eduImageFileName1 ??
        '',
      eduImageSaveName1:
        row.selfImageSaveName ??
        row.SelfImageSaveName ??
        row.SELF_IMAGE_SAVE_NAME ??
        row.eduImageSaveName1 ??
        '',
      eduImageFilePath1:
        row.selfImageFilePath ??
        row.SelfImageFilePath ??
        row.SELF_IMAGE_FILE_PATH ??
        row.eduImageFilePath1 ??
        '',
    });
    setSelfFileShowErrors(false);
    setSelfFileModalOpen(true);
    props.setSelfFileEditRowPayload?.(null);
  }, [props.selfFileEditRowPayload]);

  //#endregion
  //#region 웹

  //#endregion

  // 공통 교육 선택
  const renderEducationSelect = (
    labelText,
    placeholderText,
    fieldName,
    options = []
  ) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
        {labelText} <span style={{ color: 'red' }}>*</span>
      </Typography>

      <FormControl
        fullWidth
        required
        error={
          !educationDiaryReadOnly &&
          !props.modalRowData1?.[fieldName]?.toString().trim()
        }
      >
        <Select
          value={props.modalRowData1?.[fieldName] || ''}
          displayEmpty
          disabled={educationDiaryReadOnly}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 360,
                maxWidth: 720,
              },
            },
          }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <span style={{ color: '#9e9e9e' }}>{placeholderText}</span>
              );
            }
            const found = options.find((opt) => opt.value === selected);
            if (!found) return selected;

            return (
              <span style={{ whiteSpace: 'pre-line' }}>{found.label}</span>
            );
          }}
          onChange={(e) => {
            if (educationDiaryReadOnly) return;
            const value = e.target.value;
            let newData = {
              ...props.modalRowData1,
              [fieldName]: value,
            };

            if (fieldName === 'eduTypeCd') {
              newData.eduNmCd = '';
              newData.eduContentsCd = '';
            } else if (fieldName === 'eduNmCd') {
              newData.eduContentsCd = '';
            }

            props.setModalRowData1(newData);
          }}
        >
          {(options || []).map((item, index) => (
            <MenuItem
              key={item.value}
              value={item.value}
              sx={{
                borderBottom: '1px solid #e0e0e0',
                '&:last-of-type': {
                  borderBottom: 'none',
                },
              }}
            >
              <span style={{ whiteSpace: 'pre-line' }}>{item.label}</span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  // 공통 TextField
  const renderCommonTextField = (
    fieldName,
    { emptyHelperText, useError = false, maxLength = 100 } = {}
  ) => {
    const rawValue = props.modalRowData1?.[fieldName];
    const value =
      rawValue === null || rawValue === undefined ? '' : rawValue.toString();
    const numberFields = [
      'eduTm',
      'eduTargetMCnt',
      'eduTargetWCnt',
      'eduExecMCnt',
      'eduExecWCnt',
      'eduNoExecMCnt',
      'eduNoExecWCnt',
    ];
    const isNumberField = numberFields.includes(fieldName);

    const trimmed = value.trim();
    const showError = useError && !trimmed && !educationDiaryReadOnly;

    const helperText = isNumberField
      ? ''
      : useError
        ? showError
          ? emptyHelperText
          : `${value.length}/${maxLength}`
        : `${value.length}/${maxLength}`;

    return (
      <TextField
        value={value}
        onChange={(e) => {
          if (educationDiaryReadOnly) return;
          let newValue = e.target.value;
          if (isNumberField) {
            newValue = newValue.replace(/[^\d]/g, '').slice(0, maxLength);
          } else {
            newValue = newValue.slice(0, maxLength);
          }
          props.setModalRowData1({
            ...props.modalRowData1,
            [fieldName]: newValue,
          });
        }}
        fullWidth
        required
        error={showError}
        InputProps={{ readOnly: educationDiaryReadOnly }}
        inputProps={{
          maxLength,
        }}
        helperText={helperText}
      />
    );
  };

  // 교육 인원
  const renderEducationPersonnelRow = (leftLabel, maleField, femaleField) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Typography
        sx={{
          flex: 1,
          textAlign: 'center',
        }}
      >
        {leftLabel} <span style={{ color: 'red' }}>*</span>
      </Typography>
      <Box sx={{ flex: 1, px: 1 }}>
        {renderCommonTextField(maleField, {
          maxLength: 9,
          useError: true,
          emptyHelperText: '교육 인원을 입력해주세요.',
        })}
      </Box>
      <Box sx={{ flex: 1, px: 1 }}>
        {renderCommonTextField(femaleField, {
          maxLength: 9,
          useError: true,
          emptyHelperText: '교육 인원을 입력해주세요.',
        })}
      </Box>
      <Typography
        sx={{
          flex: 1,
          textAlign: 'center',
          fontWeight: 'bold',
        }}
      >
        {(() => {
          const getNum = (v) => {
            const n = parseInt((v ?? '').toString(), 10);
            return Number.isNaN(n) ? 0 : n;
          };
          const male = getNum(props.modalRowData1?.[maleField]);
          const female = getNum(props.modalRowData1?.[femaleField]);
          return male + female;
        })()}
      </Typography>
    </Box>
  );

  // 교육 미실시 사유
  const renderNoExecReasonRow = (fieldName) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        mb: '2px',
      }}
    >
      {renderCommonTextField(fieldName, { maxLength: 1000 })}
    </Box>
  );

  // 교육 개요 텍스트 필드
  const renderBasicInfoTextField = (label, fieldName, emptyHelperText) => (
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem' }}>
        {label}
        {['eduTm', 'eduPlace', 'eduManager'].includes(fieldName) && (
          <span style={{ color: 'red' }}> *</span>
        )}
      </Typography>
      {renderCommonTextField(fieldName, {
        emptyHelperText,
        useError: ['eduTm', 'eduPlace', 'eduManager'].includes(fieldName),
        maxLength:
          fieldName === 'eduPlace'
            ? 500
            : fieldName === 'significant'
              ? 1000
              : 100,
      })}
    </Box>
  );

  // (교육강사 선택 UI는 콤보박스 대신 참석자 명단 영역의 버튼으로 제공)

  //#region AuthButton 이벤트
  useEffect(() => {
    if (props.filterClick2) {
      if (!props.validateSelection()) {
        props.setFilterClick2(false);
        return;
      }

      props.gridRef.current.api.setQuickFilter(searchInfo.searchText);
      props.setFilterClick2(false);
    }
    if (props.searchClick2) {
      props.ResetLogic1();
      if (!props.validateSelection()) {
        props.setSearchClick2(false);
        return;
      }

      props.SearchLogic1(
        searchInfo.startDate,
        searchInfo.endDate,
        searchInfo.custCd
      );
      props.setSearchClick2(false);
    }
    if (props.addClick2) {
      if (!props.validateSelection()) {
        props.setAddClick2(false);
        return;
      }

      GridFunctions.addClick(props.gridRef);
      props.setAddClick2(false);
    }
    if (props.saveClick2) {
      if (!props.validateSelection()) {
        props.setSaveClick2(false);
        return;
      }

      props.SaveLogic1();
      props.setSaveClick2(false);
    }
    if (props.deleteClick2) {
      if (!props.validateSelection()) {
        props.setDeleteClick2(false);
        return;
      }

      props.DeleteLogic1();
      props.setDeleteClick2(false);
    }
    if (props.printClick2) {
      if (!props.validateSelection()) {
        props.setPrintClick2(false);
        return;
      }

      props.PrintLogic1();
      props.setPrintClick2(false);
    }
  }, [
    props.filterClick2,
    props.searchClick2,
    props.addClick2,
    props.saveClick2,
    props.deleteClick2,
    props.printClick2,
    props.listClick2,
  ]);

  //#endregion

  const OpenSafeOnRegistrationModal = () => {
    props.ClearQrSession?.();
    props.setModalRowData1({ ...props.INPUTS, selfYn: 'N' });
    props.setMembers?.([]);
    props.setEducationDiaryReadOnly?.(false);
    props.setModalToggle1(true);
  };

  const resetSelfFileForm = () => {
    setSelfFilePreviewOpen(false);
    setSelfFileData({
      tpmId: '',
      eduTypeCd: '',
      eduTypeNm: '',
      etcEduNm: '',
      etcEduContents: '',
      eduDt: dayjs().format('YYYY-MM-DD'),
      eduTm: '',
      eduPlace: '',
      eduManager: '',
      eduTargetMCnt: '0',
      eduTargetWCnt: '0',
      eduExecMCnt: '0',
      eduExecWCnt: '0',
      eduNoExecMCnt: '0',
      eduNoExecWCnt: '0',
      eduNoExecReason1: '',
      eduNoExecReason2: '',
      eduNoExecReason3: '',
      eduImageFileName1: '',
      eduImageSaveName1: '',
      eduImageFilePath1: '',
    });
    setSelfFileShowErrors(false);
    if (selfFileInputRef.current) {
      selfFileInputRef.current.value = '';
    }
  };

  const OpenSelfFileTrainingModal = () => {
    resetSelfFileForm();
    props.setSelfFileModalReadOnly?.(false);
    setRegistrationMethodSelectOpen(false);
    setSelfFileModalOpen(true);
  };

  const HandleSelfFileSave = async () => {
    setSelfFileShowErrors(true);
    const {
      eduTypeNm,
      etcEduNm,
      etcEduContents,
      eduImageFileName1,
      eduImageFilePath1,
    } = selfFileData;
    if (
      !eduTypeNm?.toString().trim() ||
      !etcEduNm?.toString().trim() ||
      !etcEduContents?.toString().trim()
    ) {
      return;
    }
    if (
      !eduImageFileName1?.toString().trim() ||
      !eduImageFilePath1?.toString().trim()
    ) {
      if (props.dispatch && props.showSnackbar) {
        props.dispatch(
          props.showSnackbar({
            message: '교육 관련 파일을 선택해 주세요.',
            type: 'warning',
          })
        );
      }
      return;
    }
    const ok = await props.SaveLogicSelfFile?.(selfFileData);
    if (ok) {
      setSelfFileModalOpen(false);
      resetSelfFileForm();
      props.setSelfFileModalReadOnly?.(false);
    }
  };

  const CloseSelfFileModal = () => {
    setSelfFileModalOpen(false);
    resetSelfFileForm();
    props.setSelfFileModalReadOnly?.(false);
  };

  const OpenSelfFilePicker = () => {
    if (props.selfFileModalReadOnly) return;
    selfFileInputRef.current?.click();
  };

  const HandleSelfFileInputChange = async (e) => {
    if (props.selfFileModalReadOnly) return;
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    const invalidCharRegex = /[^\w가-힣\s().\[\]_,&-]/g;

    if (ext === 'hwpx') {
      if (props.dispatch && props.showSnackbar) {
        props.dispatch(
          props.showSnackbar({
            message:
              '현재 .hwpx 파일은 미리보기를 지원하지 않습니다. 변환 후 업로드 해주세요.',
            type: 'warning',
          })
        );
      }
      return;
    }
    if (['mp4', 'avi', 'mov', 'wmv'].includes(ext)) {
      if (props.dispatch && props.showSnackbar) {
        props.dispatch(
          props.showSnackbar({
            message: '동영상 파일은 업로드할 수 없습니다.',
            type: 'warning',
          })
        );
      }
      return;
    }
    if (invalidCharRegex.test(file.name)) {
      if (props.dispatch && props.showSnackbar) {
        props.dispatch(
          props.showSnackbar({
            message: `파일명에 특수문자가 포함되어 있습니다: ${file.name}`,
            type: 'warning',
          })
        );
      }
      return;
    }

    const buffer = await file.arrayBuffer();
    const mimeType = file.type || 'application/octet-stream';
    const blob = new Blob([buffer], { type: mimeType });
    const timestamp = dayjs().format('YYYYMMDDHHmmss');
    const saveName = `${timestamp}_${file.name}`;
    const fileToSave = new File([blob], saveName, { type: mimeType });

    const previewPath =
      (await props.SaveLogicSelfFilePreview?.(fileToSave)) || '';

    if (!previewPath) {
      return;
    }

    setSelfFileData((prev) => ({
      ...prev,
      eduImageFileName1: file.name,
      eduImageSaveName1: saveName,
      eduImageFilePath1: previewPath,
    }));
  };

  const HandleSelfFileDelete = () => {
    if (props.selfFileModalReadOnly) return;
    setSelfFilePreviewOpen(false);
    setSelfFileData((prev) => ({
      ...prev,
      eduImageFileName1: '',
      eduImageSaveName1: '',
      eduImageFilePath1: '',
    }));
    if (selfFileInputRef.current) {
      selfFileInputRef.current.value = '';
    }
  };

  const HandleSelfFileDownload = () => {
    props.DownloadSelfFileLogic?.({
      fileName: selfFileData.eduImageFileName1,
      saveName: selfFileData.eduImageSaveName1,
      previewFilePath: selfFileData.eduImageFilePath1,
      filePath: selfFileData.eduImageFilePath1,
    });
  };

  const CloseSelfFilePreview = () => {
    setSelfFilePreviewOpen(false);
  };

  const HandleSelfFilePreview = () => {
    if (!selfFileData.eduImageFileName1 || !selfFileData.eduImageFilePath1) {
      if (props.dispatch && props.showSnackbar) {
        props.dispatch(
          props.showSnackbar({
            message: '먼저 파일을 선택해 주세요.',
            type: 'warning',
          })
        );
      }
      return;
    }
    setSelfFilePreviewOpen(true);
  };

  return (
    <React.Fragment>
      {/* 교육일지 등록 방식 선택 */}
      <Dialog
        open={registrationMethodSelectOpen}
        onClose={() => setRegistrationMethodSelectOpen(false)}
        title={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              pr: 0,
            }}
          >
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              교육일지 등록 방식을 선택하세요
            </Typography>
            <IconButton
              aria-label="닫기"
              size="small"
              onClick={() => setRegistrationMethodSelectOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        }
        useCloseButton={false}
        useSaveButton={false}
        fullWidth
        maxWidth="md"
        content={
          <Box sx={{ pt: 0.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                alignItems: 'stretch',
              }}
            >
              {/* 세이프온 시스템으로 간편 등록 */}
              <Paper
                elevation={2}
                sx={{
                  flex: 1,
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid #e3eaf2',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 320,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    fontWeight: 700,
                    color: 'primary.main',
                    letterSpacing: 0.5,
                  }}
                >
                  SAFEON
                </Typography>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1.5,
                    bgcolor: '#e3f2fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                  }}
                >
                  <ShieldIcon sx={{ fontSize: 32, color: '#1565c0' }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  세이프온 시스템으로 간편 등록
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  연동된 정보를 활용해 일지를 작성합니다.
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2, mb: 2, flex: 1 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    세이프온에서 진행한 교육 정보를 활용해 편리하게 일지를
                    작성할 수 있습니다.
                  </Typography>
                  <Typography component="li" variant="body2">
                    교육 증빙 자료를 한곳에서 통합 관리합니다.
                  </Typography>
                </Box>
                <Button
                  name="선택 >"
                  fullWidth
                  onClick={() => {
                    setRegistrationMethodSelectOpen(false);
                    OpenSafeOnRegistrationModal();
                  }}
                  sx={{
                    py: 1.2,
                    bgcolor: '#1565c0',
                    color: '#fff',
                    '&:hover': { bgcolor: '#0d47a1' },
                  }}
                />
              </Paper>

              {/* 자체 보유 파일로 직접 등록 */}
              <Paper
                elevation={2}
                sx={{
                  flex: 1,
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid #e8f5e9',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 320,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1.5,
                    bgcolor: '#e8f5e9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 32, color: '#2e7d32' }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  자체 보유 파일로 직접 등록
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  보유하고 계신 문서 파일을 시스템에 업로드합니다.
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2, mb: 2, flex: 1 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    직접 실시한 교육의 PDF·이미지 또는 스캔 문서를 첨부합니다.
                  </Typography>
                  <Typography component="li" variant="body2">
                    기존 양식을 유지하면서 관리 일지만 디지털화할 때 사용합니다.
                  </Typography>
                </Box>
                <Button
                  name="선택 >"
                  fullWidth
                  onClick={OpenSelfFileTrainingModal}
                  sx={{
                    py: 1.2,
                    bgcolor: '#2e7d32',
                    color: '#fff',
                    '&:hover': { bgcolor: '#1b5e20' },
                  }}
                />
              </Paper>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 2, textAlign: 'center' }}
            >
              시스템 연동 방식은 관리자 승인 후 즉시 반영됩니다.
            </Typography>
          </Box>
        }
      />

      <BusinessGridCommon
        title1={'교육일지 기본정보'}
        gridId={props.gridId1}
        getRowId={(params) => {
          return params.data.no;
        }}
        addClick1={() => {
          if (!props.validateSelection()) return;
          setRegistrationMethodSelectOpen(true);
        }}
        refreshClick1={() => {
          if (!props.validateSelection()) return;

          props.SearchLogic1(
            searchInfo.startDate,
            searchInfo.endDate,
            searchInfo.custCd
          );
        }}
        deleteClick1={(e) => {
          if (!props.validateSelection()) return;

          const newRowExists = props.selectedRowData1.some(
            (row) => row.newYn === 'Y'
          );

          if (!newRowExists) {
            props.MarkedForDeletion1(props.selectedRowData1);
          } else {
            props.NewYnErrorMsg();
          }
        }}
        gridRef={props.gridRef}
        columnDefs={columnDefs}
        rowData={props.rowData1}
        setSelectedRowData={props.setSelectedRowData1}
        setChangedRowData={props.setChangedRowData1}
        rowSelection={'multiple'}
        onCellClicked={(e) => {
          const gridApi = props.gridRef.current.api;
          const clickedRowIndex = e.rowIndex;

          const isCheckboxClick = e.event?.target?.type === 'checkbox';

          if (!isCheckboxClick) {
            gridApi.ensureIndexVisible(clickedRowIndex, 'middle');
            gridApi.setFocusedCell(clickedRowIndex, 'DATE');

            const copied = JSON.parse(JSON.stringify(e.data));
            props.handleRowSelection(copied, clickedRowIndex);

            gridApi
              .getDisplayedRowAtIndex(clickedRowIndex)
              ?.setSelected(true, false);
          }
        }}
        onSelectionChanged={() => {
          const gridApi = props.gridRef?.current?.api;
          const selectedNodes = gridApi.getSelectedNodes();
          const selectedData = selectedNodes.map((n) => n.data);
          props.selectedRowNosRef.current = new Set(
            selectedData.map((r) => r.no)
          );
          props.setSelectedRowData1(selectedData);
        }}
        onCellValueChanged={(e) => {
          const key = e.data.no;

          e.node.setData({
            ...e.data,
            editYn: 'Y',
          });

          props.setChangedRowData1?.((prev) => {
            const filtered = prev.filter((row) => row.no !== key);
            return [...filtered, { ...e.data, editYn: 'Y' }];
          });
        }}
      />
      {/* 안전보건 교육일지 팝업 창 */}
      {props.modalToggle1 && (
        <Dialog
          title={null}
          titleSx={{ display: 'none', p: 0, m: 0, height: 0, minHeight: 0 }}
          open={props.modalToggle1}
          onClose={() => {
            props.ResetLogic2?.();
          }}
          contentSx={{
            p: '0 !important',
            pt: '0 !important',
            pb: '0 !important',
            px: '0 !important',
            py: '0 !important',
            m: '0 !important',
            mt: '0 !important',
            overflow: 'hidden',
            height: '85vh',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            '&.MuiDialogContent-root': {
              padding: '0 !important',
              paddingTop: '0 !important',
              height: '85vh',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
          sx={{
            '& .MuiDialog-paper': {
              height: '90vh',
              maxHeight: '90vh',
            },
          }}
          fullWidth
          maxWidth="md"
          saveButtonName="저장"
          closeButtonName="닫기"
          useSaveButton={!educationDiaryReadOnly}
          useSaveAfterClose={false}
          useCloseButton={educationDiaryReadOnly}
          onSave={() => {
            props.SaveLogic1();
          }}
          content={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: '90vh',
                bgcolor: '#f6f7f8',
                p: 0,
                m: 0,
                mt: 0,
                pt: 0,
                overflow: 'hidden',
              }}
            >
              {/* Top Navigation Bar */}
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid #f0f2f4',
                  bgcolor: 'white',
                  px: 3,
                  py: 1.5,
                  flexShrink: 0,
                  zIndex: 20,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    color: '#111418',
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(19, 127, 236, 0.1)',
                      color: 'primary.main',
                      borderRadius: 1.5,
                    }}
                  >
                    <SchoolIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#111418',
                      fontSize: 18,
                      fontWeight: 'bold',
                      letterSpacing: -0.15,
                    }}
                  >
                    안전보건 교육일지
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => {
                    props.ResetLogic2?.();
                  }}
                  sx={{
                    color: '#5f6368',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Paper>

              {/* Main Content - Scrollable */}
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  minHeight: 0,
                }}
              >
                {/* 교육 기본 정보 영역 */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                  }}
                >
                  {renderEducationSelect(
                    '교육의 구분',
                    '교육의 구분을 선택해주세요.',
                    'eduTypeCd',
                    props.eduCategoryOptions
                  )}

                  {/* 교육명 */}
                  {props.isEtcEduType ? (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body1"
                        sx={{ mb: 1, fontWeight: 'bold' }}
                      >
                        교육명 <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      {renderCommonTextField('eduNmCd', {
                        emptyHelperText: '교육명을 입력해주세요.',
                        useError: true,
                        maxLength: 100,
                      })}
                    </Box>
                  ) : (
                    props.modalRowData1?.eduTypeCd &&
                    renderEducationSelect(
                      '교육명',
                      '교육명을 선택해주세요.',
                      'eduNmCd',
                      props.eduNameOptions
                    )
                  )}

                  {/* 교육 내용 */}
                  {props.isEtcEduType ? (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body1"
                        sx={{ mb: 1, fontWeight: 'bold' }}
                      >
                        교육 내용 <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      {renderCommonTextField('eduContentsCd', {
                        emptyHelperText: '교육 내용을 입력해주세요.',
                        useError: true,
                        maxLength: 1000,
                      })}
                    </Box>
                  ) : (
                    (props.modalRowData1?.eduNmCd || props.isEtcEduType) &&
                    renderEducationSelect(
                      '교육 내용',
                      '교육 내용을 선택해주세요.',
                      'eduContentsCd',
                      props.eduContentOptions
                    )
                  )}

                  {(props.modalRowData1?.eduContentsCd ||
                    props.isEtcEduType) && (
                    <>
                      {/* 교육 개요 */}
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ mb: 1, fontWeight: 'bold' }}
                        >
                          교육 개요
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, fontSize: '0.875rem' }}
                            >
                              교육일 (YYYY-MM-DD){' '}
                              <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <DatePicker
                              placeholder="교육일"
                              disabled={educationDiaryReadOnly}
                              value={
                                props.modalRowData1?.eduDt
                                  ? dayjs(
                                      props.modalRowData1.eduDt,
                                      'YYYY-MM-DD'
                                    )
                                  : dayjs()
                              }
                              onChange={(date) => {
                                if (educationDiaryReadOnly) return;
                                const newData = {
                                  ...props.modalRowData1,
                                  eduDt: date
                                    ? date.format('YYYY-MM-DD')
                                    : dayjs().format('YYYY-MM-DD'),
                                };
                                props.setModalRowData1(newData);
                              }}
                              format="YYYY-MM-DD"
                              views={['year', 'month', 'day']}
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                          </Box>

                          {renderBasicInfoTextField(
                            '교육시간 (시간)',
                            'eduTm',
                            '교육시간을 입력해주세요.'
                          )}
                          {renderBasicInfoTextField(
                            '교육장소',
                            'eduPlace',
                            '교육장소를 입력해주세요.'
                          )}
                          {renderBasicInfoTextField(
                            '교육담당자',
                            'eduManager',
                            '교육담당자를 입력해주세요.'
                          )}
                        </Box>
                      </Box>

                      {/* 교육 인원 */}
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ mb: 1, fontWeight: 'bold' }}
                        >
                          교육 인원
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flex: 1,
                              width: '100%',
                            }}
                          >
                            {['구분', '남', '여', '계'].map((label) => (
                              <Typography
                                key={label}
                                sx={{
                                  flex: 1,
                                  textAlign: 'center',
                                }}
                              >
                                {label}
                              </Typography>
                            ))}
                          </Box>

                          {renderEducationPersonnelRow(
                            '교육 대상자 수',
                            'eduTargetMCnt',
                            'eduTargetWCnt'
                          )}
                          {renderEducationPersonnelRow(
                            '교육 실시자 수',
                            'eduExecMCnt',
                            'eduExecWCnt'
                          )}
                          {renderEducationPersonnelRow(
                            '교육 미실시자 수',
                            'eduNoExecMCnt',
                            'eduNoExecWCnt'
                          )}
                        </Box>
                        <Box sx={{ mt: 2, flex: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{ mb: 1, fontWeight: 'bold' }}
                          >
                            교육 미실시 사유
                          </Typography>

                          {[{ field: 'eduNoExecReason1' }].map(({ field }) =>
                            renderNoExecReasonRow(field)
                          )}
                        </Box>
                      </Box>
                    </>
                  )}
                </Paper>

                {(props.modalRowData1?.eduContentsCd || props.isEtcEduType) && (
                  <>
                    {/* 참석자 명단 */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mt: 2,
                        bgcolor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 2,
                          pb: 2,
                          borderBottom: '1px solid #f0f2f4',
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: '#111418',
                            fontWeight: 600,
                            fontSize: '1rem',
                          }}
                        >
                          안전보건교육 참석자 명단
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Button
                            variant="outlined"
                            startIcon={<QrCodeIcon />}
                            name="QR 생성"
                            disabled={educationDiaryReadOnly}
                            onClick={() => {
                              if (!props.validateSelection?.()) return;
                              props.LogicQrCreate?.();
                            }}
                            sx={{
                              borderColor: '#1976d2',
                              color: '#1976d2',
                              fontWeight: 500,
                              '&:hover': {
                                borderColor: '#1565c0',
                                bgcolor: 'rgba(25, 118, 210, 0.06)',
                              },
                            }}
                          />

                          {String(
                            props.modalRowData1?.selfYn ??
                              props.modalRowData1?.SELF_YN ??
                              'N'
                          ).toUpperCase() !== 'Y' && (
                            <>
                              <TextField
                                size="small"
                                value={
                                  props.modalRowData1?.eduInstructorNm || ''
                                }
                                label="선택된 교육강사"
                                required
                                InputProps={{
                                  readOnly: true,
                                }}
                                sx={{
                                  width: 120,
                                  bgcolor: 'white',
                                  '& .MuiInputBase-root': {
                                    height: 36,
                                  },
                                }}
                              />
                              <Button
                                variant="outlined"
                                startIcon={<AccountCircleIcon />}
                                name="교육강사 선택"
                                disabled={educationDiaryReadOnly}
                                onClick={() => {
                                  if (!props.validateSelection?.()) return;
                                  props.OpenInstructorMemberPicker?.();
                                }}
                                sx={{
                                  borderColor: '#1976d2',
                                  color: '#1976d2',
                                  fontWeight: 500,
                                  '&:hover': {
                                    borderColor: '#1565c0',
                                    bgcolor: 'rgba(25, 118, 210, 0.06)',
                                  },
                                }}
                              />
                            </>
                          )}
                          <Button
                            variant="contained"
                            startIcon={<PersonAddIcon />}
                            name="참석자 추가"
                            disabled={educationDiaryReadOnly}
                            onClick={() => {
                              if (!props.validateSelection?.()) return;
                              props.SearchLogic3?.();
                              props.setModalToggle3(true);
                            }}
                            sx={{
                              bgcolor: '#1976d2',
                              '&:hover': {
                                bgcolor: '#1565c0',
                              },
                              fontWeight: 500,
                            }}
                          />
                        </Stack>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: 'bold',
                            color: 'text.secondary',
                          }}
                        >
                          <Box sx={{ width: 40, textAlign: 'center', pl: 2.2 }}>
                            No
                          </Box>
                          <Box sx={{ flex: 1, pl: 2 }}>소속</Box>
                          <Box sx={{ flex: 1.2, pl: 2 }}>직위</Box>
                          <Box sx={{ flex: 1.2, pl: 2 }}>성명</Box>
                          <Box sx={{ flex: 2, pl: 3 }}>서명</Box>
                          <Box sx={{ flex: 1, pl: 2 }}>PC사인</Box>
                          <Box sx={{ width: 48 }} />
                        </Box>

                        {(props.members || [])
                          .filter((m) => !m.deleted)
                          .map((member, idx) => (
                            <Box
                              key={member.qrDetId || member.tpdId || idx}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 1,
                                bgcolor: '#ffffff',
                                borderRadius: 2,
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                  borderColor: '#1976d2',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: 40,
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  color: '#1976d2',
                                }}
                              >
                                {idx + 1}
                              </Box>

                              <Box sx={{ flex: 1, px: 1 }}>
                                <Typography
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontSize: '0.95rem',
                                  }}
                                >
                                  {member.depNm || ''}
                                </Typography>
                              </Box>

                              <Box sx={{ flex: 1.2, px: 1 }}>
                                <Typography
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontSize: '0.95rem',
                                  }}
                                >
                                  {member.position || member.jobType || ''}
                                </Typography>
                              </Box>

                              <Box sx={{ flex: 1.2, px: 1 }}>
                                <Typography
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontSize: '0.95rem',
                                  }}
                                >
                                  {member.userNm || ''}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  flex: 2,
                                  px: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                {String(
                                  member.signYn ??
                                    member.SIGN_YN ??
                                    'N'
                                ).toUpperCase() === 'Y' &&
                                (member.signPath ?? member.SIGN_PATH) ? (
                                  <img
                                    src={(() => {
                                      const sp =
                                        member.signPath ??
                                        member.SIGN_PATH ??
                                        '';
                                      const spStr = String(sp);
                                      return spStr.startsWith('data:')
                                        ? spStr
                                        : Global.file.useUrl +
                                            'files/' +
                                            Commons.EncodeUrl(spStr);
                                    })()}
                                    alt="서명"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: 48,
                                      objectFit: 'contain',
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 4,
                                      padding: 2,
                                    }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      bgcolor: '#eceff1',
                                      color: '#546e7a',
                                      px: 1,
                                      py: '4px',
                                      borderRadius: 1,
                                      fontWeight: 700,
                                      fontSize: '0.75rem',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    서명대기
                                  </Box>
                                )}
                              </Box>

                              <Box sx={{ flex: 1, px: 1 }}>
                                <Button
                                  variant="outlined"
                                  name="PC사인"
                                  size="small"
                                  disabled={
                                    educationDiaryReadOnly ||
                                    String(
                                      member.signYn ??
                                        member.SIGN_YN ??
                                        'N'
                                    ).toUpperCase() === 'Y'
                                  }
                                  onClick={() => {
                                    setPcSignTargetMember(member);
                                    setPcSignModalOpen(true);
                                  }}
                                  sx={{
                                    width: '100%',
                                  }}
                                />
                              </Box>

                              <IconButton
                                size="small"
                                disabled={educationDiaryReadOnly}
                                onClick={() => {
                                  props.setMembers?.((prev = []) => {
                                    const originalIndex = prev.findIndex(
                                      (m) => m === member
                                    );
                                    if (originalIndex === -1) return prev;

                                    const target = prev[originalIndex];

                                    if (target.tpdId) {
                                      const next = [...prev];
                                      next[originalIndex] = {
                                        ...next[originalIndex],
                                        deleted: true,
                                      };
                                      return next;
                                    }

                                    const filtered = prev.filter(
                                      (_, i) => i !== originalIndex
                                    );
                                    return filtered.map((m, i2) => ({
                                      ...m,
                                      no: i2 + 1,
                                    }));
                                  });
                                }}
                                sx={{
                                  color: 'error.main',
                                  ml: 1,
                                  '&:hover': {
                                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s ease-in-out',
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}

                        {(!props.members || props.members.length === 0) && (
                          <Box
                            sx={{
                              textAlign: 'center',
                              py: 6,
                              bgcolor: '#ffffff',
                              borderRadius: 2,
                              border: '2px dashed #e0e0e0',
                            }}
                          >
                            <Typography
                              variant="h6"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              안전보건교육 참석자를 추가해 주세요.
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              상단의 '참석자 추가' 버튼을 클릭해 참석자를 선택해
                              주세요.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>

                    {/* 사진 대지 업로드 영역 */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mt: 2,
                        bgcolor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                      }}
                    >
                      <Box
                        sx={{
                          mb: 2,
                          pb: 2,
                          borderBottom: '1px solid #f0f2f4',
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: '#111418',
                            fontSize: '1rem',
                            mb: 0.5,
                          }}
                        >
                          사진 대지
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#5f6368' }}>
                          사진을 최대 2장까지 업로드할 수 있습니다.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          flexWrap: 'wrap',
                        }}
                      >
                        {[
                          {
                            label: '사진 1',
                            preview: props.photoPreview1,
                            setPreview: props.setPhotoPreview1,
                            inputId: 'edu-photo-1',
                            slotIndex: 1,
                          },
                          {
                            label: '사진 2',
                            preview: props.photoPreview2,
                            setPreview: props.setPhotoPreview2,
                            inputId: 'edu-photo-2',
                            slotIndex: 2,
                          },
                        ].map(
                          ({
                            label,
                            preview,
                            setPreview,
                            inputId,
                            slotIndex,
                          }) => (
                            <Paper
                              key={inputId}
                              sx={{
                                width: 240,
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                              }}
                              variant="outlined"
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 160,
                                  mb: 2,
                                  bgcolor: '#fafafa',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  overflow: 'hidden',
                                  border: '1px dashed #ddd',
                                }}
                              >
                                {preview ? (
                                  <img
                                    src={preview}
                                    alt={label}
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '100%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ textAlign: 'center' }}
                                  >
                                    {label}를 업로드해주세요.
                                  </Typography>
                                )}
                              </Box>

                              <input
                                id={inputId}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) =>
                                  props.handlePhotoChange(
                                    e,
                                    setPreview,
                                    slotIndex
                                  )
                                }
                              />

                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  width: '100%',
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  disabled={educationDiaryReadOnly}
                                  onClick={() =>
                                    document.getElementById(inputId)?.click()
                                  }
                                  sx={{ flex: 1 }}
                                  name={'사진 파일 추가'}
                                />
                                <Button
                                  variant="outlined"
                                  color="error"
                                  disabled={
                                    educationDiaryReadOnly || !preview
                                  }
                                  onClick={() => props.DeletePhoto?.(slotIndex)}
                                  sx={{ minWidth: 80 }}
                                  name={'사진 삭제'}
                                >
                                  삭제
                                </Button>
                              </Box>
                            </Paper>
                          )
                        )}
                      </Box>
                    </Paper>

                    {/* 특이사항 */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mt: 2,
                        bgcolor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                      }}
                    >
                      {renderBasicInfoTextField(
                        '특이사항',
                        'significant',
                        '특이사항을 입력해주세요.'
                      )}
                    </Paper>
                  </>
                )}
              </Box>

              {props.modalToggle3 && (
                <Dialog
                  title={null}
                  titleSx={{
                    display: 'none',
                    p: 0,
                    m: 0,
                    height: 0,
                    minHeight: 0,
                  }}
                  open={props.modalToggle3}
                  onClose={() => {
                    setSelectedWorkers([]);
                    setSearchKeyword('');
                    props.setModalToggle3(false);
                  }}
                  contentSx={{
                    p: '0 !important',
                    pt: '0 !important',
                    pb: '0 !important',
                    px: '0 !important',
                    py: '0 !important',
                    m: '0 !important',
                    mt: '0 !important',
                    overflow: 'hidden',
                    '&.MuiDialogContent-root': {
                      padding: '0 !important',
                      paddingTop: '0 !important',
                    },
                  }}
                  fullWidth
                  maxWidth="lg"
                  saveButtonName="확인"
                  closeButtonName="취소"
                  useSaveButton={true}
                  useSaveAfterClose={false}
                  useCloseButton={false}
                  onSave={() => {
                    const originalMembers = props.members || [];
                    const allWorkers = selectedWorkers || [];

                    const existingNonDeletedIds = new Set(
                      originalMembers
                        .filter((m) => !m.deleted && m.tpaUserId)
                        .map((m) => m.tpaUserId.toString())
                    );

                    let duplicateCount = 0;
                    allWorkers.forEach((w) => {
                      if (!w?.id) return;
                      if (existingNonDeletedIds.has(w.id.toString()))
                        duplicateCount += 1;
                    });

                    const byUserId = (id) =>
                      originalMembers.find(
                        (m) => m.tpaUserId && m.tpaUserId.toString() === id
                      );

                    const nextMembers = [...originalMembers];

                    allWorkers.forEach((worker) => {
                      const id = worker.id ? worker.id.toString() : null;
                      if (!id) return;

                      const found = byUserId(id);
                      if (found) {
                        Object.assign(found, {
                          deleted: false,
                          // backend 저장 필드는 jobType/remark 구조를 유지하되,
                          // 화면에서 사용할 소속/직위를 별도 필드로 추가로 보관
                          depNm: worker.depNm || found.depNm,
                          position: worker.position || found.position,
                          jobType:
                            worker.position || worker.depNm || found.jobType,
                          userNm: worker.name || found.userNm,
                          tpaUserId: id,
                        });
                      }
                    });

                    const existingIdsAfterRestore = new Set(
                      nextMembers
                        .filter((m) => m.tpaUserId)
                        .map((m) => m.tpaUserId.toString())
                    );

                    const newMembersFromSelection = allWorkers
                      .filter(
                        (w) =>
                          w.id && !existingIdsAfterRestore.has(w.id.toString())
                      )
                      .map((w) => ({
                        tpdId: '',
                        no: 0,
                        depNm: w.depNm || '',
                        position: w.position || '',
                        jobType: w.position || w.depNm || '',
                        tpaUserId: w.id,
                        userNm: w.name || '',
                        remark: '',
                        signYn: 'N',
                        signPath: '',
                      }));

                    const mergedMembers = [
                      ...nextMembers,
                      ...newMembersFromSelection,
                    ].map((m, idx) => ({ ...m, no: idx + 1 }));

                    if (duplicateCount > 0) {
                      props.dispatch?.(
                        props.showSnackbar({
                          message: `${duplicateCount}명의 중복된 참석자가 제외되었습니다.`,
                          type: 'warning',
                        })
                      );
                    }

                    props.setMembers?.(mergedMembers);
                    setSelectedWorkers([]);
                    setSearchKeyword('');
                    props.setModalToggle3(false);
                  }}
                  content={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        bgcolor: '#f6f7f8',
                        p: 0,
                        m: 0,
                        mt: 0,
                        pt: 0,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Top Navigation Bar */}
                      <Paper
                        elevation={0}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderBottom: '1px solid #f0f2f4',
                          bgcolor: 'white',
                          px: 3,
                          py: 1,
                          flexShrink: 0,
                          zIndex: 20,
                          m: 0,
                          mt: 0,
                          pt: 1,
                          pb: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: '#111418',
                          }}
                        >
                          참석인원 추가
                        </Typography>
                        <IconButton
                          onClick={() => {
                            setSelectedWorkers([]);
                            setSearchKeyword('');
                            props.setModalToggle3(false);
                          }}
                          sx={{
                            color: '#5f6368',
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Paper>

                      {/* Main Content */}
                      <Box
                        sx={{
                          display: 'flex',
                          flex: 1,
                          gap: 2,
                          p: 2,
                          overflow: 'hidden',
                        }}
                      >
                        {/* 왼쪽: 검색 결과 리스트 */}
                        <Paper
                          elevation={0}
                          sx={{
                            flex: 1,
                            p: 2,
                            minWidth: '300px',
                            bgcolor: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              mb: 2,
                            }}
                          >
                            <TextField
                              placeholder="이름 또는 사번으로 검색"
                              variant="outlined"
                              size="small"
                              sx={{ flex: 1 }}
                              value={searchKeyword}
                              onChange={(e) => setSearchKeyword(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  props.SearchLogic3?.();
                                }
                              }}
                            />
                            <Button
                              variant="contained"
                              onClick={() => {
                                props.SearchLogic3?.();
                              }}
                              sx={{
                                minWidth: '80px',
                                bgcolor: '#1976d2',
                                '&:hover': {
                                  bgcolor: '#1565c0',
                                },
                              }}
                            >
                              검색
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                const allWorkers = props.modalRowData3 || [];
                                setSelectedWorkers([...allWorkers]);
                              }}
                              sx={{
                                minWidth: '80px',
                                borderColor: '#e0e0e0',
                                color: '#5f6368',
                                '&:hover': {
                                  borderColor: '#1976d2',
                                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                                },
                              }}
                            >
                              전체선택
                            </Button>
                          </Box>
                          <div
                            className="ag-theme-alpine"
                            style={{ height: '550px', width: '100%' }}
                          >
                            <BusinessGridCommon
                              addYn1={'N'}
                              deleteYn1={'N'}
                              refreshYn1={'N'}
                              onCellClicked={(params) => {
                                const worker = params.data;
                                const isSelected = selectedWorkers.some(
                                  (w) => w.id === worker.id
                                );

                                if (isSelected) {
                                  setSelectedWorkers(
                                    selectedWorkers.filter(
                                      (w) => w.id !== worker.id
                                    )
                                  );
                                } else {
                                  setSelectedWorkers([
                                    ...selectedWorkers,
                                    worker,
                                  ]);
                                }
                              }}
                              columnDefs={[
                                {
                                  field: 'id',
                                  headerName: 'ID',
                                  maxWidth: 150,
                                },
                                {
                                  field: 'name',
                                  headerName: '이름',
                                  maxWidth: 150,
                                },
                                {
                                  field: 'position',
                                  headerName: '직책',
                                  maxWidth: 150,
                                },
                                {
                                  field: 'depNm',
                                  headerName: '소속',
                                  maxWidth: 150,
                                },
                                {
                                  field: 'tel',
                                  headerName: '전화번호',
                                  maxWidth: 150,
                                },
                              ]}
                              rowData={(props.modalRowData3 || []).filter(
                                (worker) => {
                                  if (!searchKeyword.trim()) return true;
                                  const keyword = searchKeyword.toLowerCase();
                                  return (
                                    worker.NAME?.toLowerCase().includes(
                                      keyword
                                    ) ||
                                    worker.EMP_NO?.toLowerCase().includes(
                                      keyword
                                    ) ||
                                    worker.DEP_NM?.toLowerCase().includes(
                                      keyword
                                    )
                                  );
                                }
                              )}
                              defaultColDef={{
                                minWidth: 80,
                                resizable: true,
                                sortable: true,
                              }}
                            />
                          </div>
                        </Paper>

                        {/* 선택된 항목 리스트 */}
                        <Paper
                          elevation={0}
                          sx={{
                            width: '400px',
                            p: 2,
                            bgcolor: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 2,
                              pb: 2,
                              borderBottom: '1px solid #f0f2f4',
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: '#111418',
                              }}
                            >
                              선택된 참석자 ({selectedWorkers.length}명)
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setSelectedWorkers([]);
                              }}
                              disabled={selectedWorkers.length === 0}
                              sx={{
                                borderColor: '#e0e0e0',
                                color: '#5f6368',
                                '&:hover': {
                                  borderColor: '#d32f2f',
                                  color: '#d32f2f',
                                  bgcolor: 'rgba(211, 47, 47, 0.04)',
                                },
                              }}
                            >
                              전체삭제
                            </Button>
                          </Box>
                          <Box
                            sx={{
                              flex: 1,
                              overflow: 'auto',
                              bgcolor: '#f6f7f8',
                              borderRadius: 1,
                              p: 1,
                            }}
                          >
                            {selectedWorkers.map((worker, index) => (
                              <Paper
                                key={worker.id || index}
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  bgcolor: 'white',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 1,
                                  position: 'relative',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <AccountCircleIcon
                                  sx={{
                                    fontSize: 40,
                                    color: '#1976d2',
                                    mr: 1.5,
                                  }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: '#111418' }}
                                  >
                                    {worker.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#5f6368' }}
                                  >
                                    {worker.depNm}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedWorkers(
                                      selectedWorkers.filter(
                                        (_, i) => i !== index
                                      )
                                    );
                                  }}
                                  sx={{
                                    color: '#5f6368',
                                    '&:hover': {
                                      color: '#d32f2f',
                                      bgcolor: 'rgba(211, 47, 47, 0.04)',
                                    },
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Paper>
                            ))}
                            {selectedWorkers.length === 0 && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: '100%',
                                  textAlign: 'center',
                                  py: 4,
                                }}
                              >
                                <PersonAddIcon
                                  sx={{
                                    fontSize: 48,
                                    color: '#9aa0a6',
                                    mb: 2,
                                  }}
                                />
                                <Typography
                                  variant="body1"
                                  sx={{ color: '#5f6368', mb: 1 }}
                                >
                                  선택한 참석자가 없어요
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#9aa0a6' }}
                                >
                                  선택된 항목을 여기에서 확인하실 수 있어요
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </Box>
                    </Box>
                  }
                />
              )}
            </Box>
          }
        />
      )}

      {/* PC사인 팝업 */}
      {pcSignModalOpen && (
        <Dialog
          title={null}
          titleSx={{ display: 'none', p: 0, m: 0, height: 0, minHeight: 0 }}
          open={pcSignModalOpen}
          onClose={ClosePcSignModal}
          contentSx={{
            p: '0 !important',
            overflow: 'hidden',
          }}
          sx={{
            '& .MuiDialog-paper': {
              height: '90vh',
              maxHeight: '90vh',
            },
          }}
          fullWidth
          maxWidth="md"
          saveButtonName="완료"
          useSaveButton={true}
          useSaveAfterClose={false}
          useCloseButton={false}
          onSave={HandlePcSignConfirm}
          content={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: '90vh',
                bgcolor: '#f6f7f8',
                p: 0,
                m: 0,
                mt: 0,
                pt: 0,
                overflow: 'hidden',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid #f0f2f4',
                  bgcolor: 'white',
                  px: 3,
                  py: 1.5,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AccountCircleIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography sx={{ fontWeight: 700, color: '#111418' }}>
                    PC사인
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {pcSignTargetMember?.userNm || ''}
                  </Typography>
                </Box>
                <Button
                  name="취소"
                  variant="outlined"
                  onClick={ClosePcSignModal}
                  sx={{ minWidth: 72 }}
                />
              </Paper>

              <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  터치펜·마우스로 서명합니다.
                </Typography>

                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    p: 1,
                    bgcolor: '#fafafa',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      '& .sigCanvas': { display: 'block', width: '100% !important' },
                    }}
                  >
                    <SignatureCanvas
                      ref={pcSignSignatureRef}
                      penColor="black"
                      minWidth={2}
                      maxWidth={4}
                      throttle={16}
                      canvasProps={{
                        className: 'sigCanvas',
                        style: {
                          height: 260,
                          width: '100%',
                          borderRadius: 8,
                          backgroundColor: '#E4EFED',
                          border: '1px solid #bdbdbd',
                          touchAction: 'none',
                        },
                      }}
                      onEnd={() => setPcSignError(false)}
                    />

                    <Typography
                      sx={{
                        fontSize: '2.2rem',
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none',
                        color: 'rgba(0, 0, 0, 0.08)',
                        fontWeight: 600,
                        maxWidth: '85%',
                        textAlign: 'center',
                        wordBreak: 'keep-all',
                      }}
                    >
                      {pcSignTargetMember?.userNm || ''}
                    </Typography>
                  </Box>
                </Box>

                {pcSignError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                    서명을 입력한 뒤 확인을 눌러 주세요.
                  </Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    name="지우기"
                    variant="outlined"
                    onClick={() => {
                      pcSignSignatureRef.current?.clear();
                      setPcSignError(false);
                    }}
                    sx={{ minWidth: 88 }}
                  />
                </Box>
              </Box>
            </Box>
          }
        />
      )}

      {/* 자체 보유 파일 — 자체 교육 일지 (안전보건 교육일지와 동일 셸, 구성은 별도) */}
      {selfFileModalOpen && (
        <Dialog
          title={null}
          titleSx={{ display: 'none', p: 0, m: 0, height: 0, minHeight: 0 }}
          open={selfFileModalOpen}
          onClose={CloseSelfFileModal}
          contentSx={{
            p: '0 !important',
            pt: '0 !important',
            pb: '0 !important',
            px: '0 !important',
            py: '0 !important',
            m: '0 !important',
            mt: '0 !important',
            overflow: 'hidden',
            height: '85vh',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            '&.MuiDialogContent-root': {
              padding: '0 !important',
              paddingTop: '0 !important',
              height: '85vh',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
          sx={{
            '& .MuiDialog-paper': {
              height: '90vh',
              maxHeight: '90vh',
            },
          }}
          fullWidth
          maxWidth="md"
          saveButtonName="저장"
          closeButtonName="닫기"
          useSaveButton={!props.selfFileModalReadOnly}
          useSaveAfterClose={false}
          useCloseButton={!!props.selfFileModalReadOnly}
          onSave={HandleSelfFileSave}
          content={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: '90vh',
                bgcolor: '#f6f7f8',
                p: 0,
                m: 0,
                mt: 0,
                pt: 0,
                overflow: 'hidden',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid #f0f2f4',
                  bgcolor: 'white',
                  px: 3,
                  py: 1.5,
                  flexShrink: 0,
                  zIndex: 20,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    color: '#111418',
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(19, 127, 236, 0.1)',
                      color: 'primary.main',
                      borderRadius: 1.5,
                    }}
                  >
                    <SchoolIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#111418',
                      fontSize: 18,
                      fontWeight: 'bold',
                      letterSpacing: -0.15,
                    }}
                  >
                    안전보건 교육일지
                  </Typography>
                </Box>
                <IconButton
                  onClick={CloseSelfFileModal}
                  sx={{
                    color: '#5f6368',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Paper>

              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  minHeight: 0,
                }}
              >
                {/* 교육 기본 정보 영역 */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="파일을 선택하세요"
                      value={selfFileData.eduImageFileName1}
                      onClick={
                        props.selfFileModalReadOnly ? undefined : OpenSelfFilePicker
                      }
                      InputProps={{ readOnly: true }}
                      sx={{
                        cursor: props.selfFileModalReadOnly ? 'default' : 'pointer',
                      }}
                    />
                    <IconButton
                      size="small"
                      aria-label="파일 삭제"
                      onClick={HandleSelfFileDelete}
                      disabled={
                        !selfFileData.eduImageFileName1 ||
                        props.selfFileModalReadOnly
                      }
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                    <Button
                      name="파일 다운로드"
                      onClick={HandleSelfFileDownload}
                      disabled={!selfFileData.eduImageFileName1}
                      sx={{
                        width: '30%',
                        px: 1.5,
                        py: 0.75,
                        mr: 1,
                        bgcolor: '#2e7d32',
                        color: '#fff',
                        '&:hover': { bgcolor: '#1b5e20' },
                        '&.Mui-disabled': {
                          bgcolor: '#e0e0e0',
                          color: '#9e9e9e',
                        },
                      }}
                      startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                    />
                    <Button
                      name="파일미리보기"
                      onClick={HandleSelfFilePreview}
                      disabled={
                        !selfFileData.eduImageFileName1 ||
                        !selfFileData.eduImageFilePath1
                      }
                      sx={{
                        flexShrink: 0,
                        px: 1.5,
                        py: 0.75,
                        bgcolor: '#1976d2',
                        color: '#fff',
                        '&:hover': { bgcolor: '#1565c0' },
                        '&.Mui-disabled': {
                          bgcolor: '#e0e0e0',
                          color: '#9e9e9e',
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      교육의 구분 <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      value={selfFileData.eduTypeNm}
                      onChange={(e) =>
                        setSelfFileData((prev) => ({
                          ...prev,
                          eduTypeNm: e.target.value.slice(0, 100),
                        }))
                      }
                      inputProps={{ maxLength: 100 }}
                      InputProps={{
                        readOnly: !!props.selfFileModalReadOnly,
                      }}
                      error={
                        selfFileShowErrors &&
                        !selfFileData.eduTypeNm?.toString().trim()
                      }
                      helperText={
                        selfFileShowErrors &&
                        !selfFileData.eduTypeNm?.toString().trim()
                          ? '교육의 구분을 입력해주세요.'
                          : `${(selfFileData.eduTypeNm || '').length}/100`
                      }
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      교육명 <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      value={selfFileData.etcEduNm}
                      onChange={(e) =>
                        setSelfFileData((prev) => ({
                          ...prev,
                          etcEduNm: e.target.value.slice(0, 100),
                        }))
                      }
                      inputProps={{ maxLength: 100 }}
                      InputProps={{
                        readOnly: !!props.selfFileModalReadOnly,
                      }}
                      error={
                        selfFileShowErrors &&
                        !selfFileData.etcEduNm?.toString().trim()
                      }
                      helperText={
                        selfFileShowErrors &&
                        !selfFileData.etcEduNm?.toString().trim()
                          ? '교육명을 입력해주세요.'
                          : `${(selfFileData.etcEduNm || '').length}/100`
                      }
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      교육 내용 <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      multiline
                      minRows={3}
                      value={selfFileData.etcEduContents}
                      onChange={(e) =>
                        setSelfFileData((prev) => ({
                          ...prev,
                          etcEduContents: e.target.value.slice(0, 1000),
                        }))
                      }
                      inputProps={{ maxLength: 1000 }}
                      InputProps={{
                        readOnly: !!props.selfFileModalReadOnly,
                      }}
                      error={
                        selfFileShowErrors &&
                        !selfFileData.etcEduContents?.toString().trim()
                      }
                      helperText={
                        selfFileShowErrors &&
                        !selfFileData.etcEduContents?.toString().trim()
                          ? '교육 내용을 입력해주세요.'
                          : `${(selfFileData.etcEduContents || '').length}/1000`
                      }
                    />
                  </Box>
                </Paper>
                <input
                  ref={selfFileInputRef}
                  type="file"
                  hidden
                  onChange={HandleSelfFileInputChange}
                />
              </Box>
            </Box>
          }
        />
      )}

      <TRA_CUSTOM_02_ApprovalModal
        open={!!props.approvalModalOpen}
        onClose={props.CloseApprovalModal}
        userInfo={props.userInfo}
        intermediateApprovers={props.approvalIntermediate ?? []}
        finalApprover={props.approvalFinal}
        approvalSignStatus={props.approvalSignStatus}
        onRemoveIntermediate={props.RemoveApprovalIntermediate}
        onOpenIntermediatePicker={() =>
          props.OpenApprovalMemberPicker?.('intermediate')
        }
        onOpenFinalPicker={() => props.OpenApprovalMemberPicker?.('final')}
        validateBeforeSignStep={props.ValidateApprovalBeforeSign}
        onSubmit={props.SubmitApprovalRequest}
      />

      <TRA_CUSTOM_02_ApproverSignModal
        open={!!props.approverSignModalOpen}
        onClose={props.CloseApproverSignModal}
        onConfirm={props.SubmitApproverSign}
        onRequestReject={props.OpenApproverRejectFromSign}
        title="결재하기"
      />

      <TRA_CUSTOM_02_ApproverRejectModal
        open={!!props.approverRejectModalOpen}
        onClose={props.CloseApproverRejectModal}
        onConfirm={props.SubmitApproverReject}
        title="결재 반려"
      />

      <TRA_CUSTOM_02_ReturnHistoryModal
        open={!!props.returnHistoryModalOpen}
        onClose={props.CloseReturnHistoryModal}
        rows={props.returnHistoryRows}
      />

      {selfFilePreviewOpen && (
        <Dialog
          open={selfFilePreviewOpen}
          onClose={CloseSelfFilePreview}
          title={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                pr: 0,
              }}
            >
              <Typography
                variant="h6"
                component="span"
                sx={{ fontWeight: 600 }}
              >
                파일 미리보기
              </Typography>
            </Box>
          }
          fullWidth
          maxWidth="lg"
          useSaveButton={false}
          useCloseButton={true}
          closeButtonName="닫기"
          contentSx={{
            p: 2,
            pt: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
          content={
            <Box
              sx={{
                minHeight: '58vh',
                maxHeight: '80vh',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'auto',
                bgcolor: '#f5f5f5',
                p: 1,
              }}
            >
              <FilePreview
                selectedRowData={selfFilePreviewRow}
                fileNameField="fileName"
                filePathField="previewFilePath"
                filePreviewHeight="560px"
              />
            </Box>
          }
        />
      )}
    </React.Fragment>
  );
}

export default TRA_CUSTOM_02;
